import React, { useEffect, useState } from "react";
import {
  Box, Paper, Typography, TextField, Button, Tabs, Tab, Stack, Switch, FormControlLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Table, TableHead, TableBody, TableRow, TableCell,
  Checkbox, IconButton, Chip, Tooltip,
} from "@mui/material";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { slaService } from "../../api/services/slaService";
import { PageHeader, Loader, ConfirmDialog } from "../../components/Common";
import DataTable from "../../components/DataTable";

export default function WorkflowSettingsPage() {
  const [tab, setTab] = useState(0);
  return (
    <Box>
      <PageHeader title="Workflow" subtitle="Ticket statuses and the transitions allowed between them" />
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: "1px solid #eee" }}>
        <Tab label="Statuses" />
        <Tab label="Transitions" />
      </Tabs>
      {tab === 0 && <StatusesTab />}
      {tab === 1 && <TransitionsTab />}
    </Box>
  );
}

const emptyStatusForm = { name: "", state_group: "open", sort_order: 0, is_default: false };

function StatusesTab() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { data: statuses, isLoading } = useQuery({ queryKey: ["statuses"], queryFn: slaService.listStatuses });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyStatusForm);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["statuses"] });

  const saveMutation = useMutation({
    mutationFn: () =>
      editing
        ? slaService.updateStatus(editing.id, form)
        : slaService.createStatus(form),
    onSuccess: () => {
      enqueueSnackbar(editing ? "Status updated" : "Status created", { variant: "success" });
      setDialogOpen(false);
      invalidate();
    },
    onError: (err) => enqueueSnackbar(err.response?.data?.detail || "Couldn't save status", { variant: "error" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => slaService.deleteStatus(id),
    onSuccess: () => {
      enqueueSnackbar("Status deleted", { variant: "success" });
      setDeleteTarget(null);
      invalidate();
    },
    onError: (err) => {
      enqueueSnackbar(err.response?.data?.detail || "Couldn't delete status", { variant: "error" });
      setDeleteTarget(null);
    },
  });

  const openCreate = () => { setEditing(null); setForm(emptyStatusForm); setDialogOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ name: s.name, state_group: s.state_group, sort_order: s.sort_order, is_default: s.is_default });
    setDialogOpen(true);
  };

  const columns = [
    { key: "sort_order", label: "Order" },
    { key: "name", label: "Name", render: (s) => <strong>{s.name}</strong> },
    { key: "state_group", label: "Group", render: (s) => <Chip size="small" label={s.state_group} /> },
    { key: "is_default", label: "Default", render: (s) => (s.is_default ? "Yes" : "") },
    {
      key: "actions", label: "", width: 90,
      render: (s) => (
        <Stack direction="row" spacing={0.5}>
          <IconButton size="small" onClick={() => openEdit(s)}><Pencil size={16} /></IconButton>
          <IconButton size="small" color="error" onClick={() => setDeleteTarget(s)}><Trash2 size={16} /></IconButton>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button variant="contained" startIcon={<Plus size={16} />} onClick={openCreate}>New Status</Button>
      </Stack>

      <DataTable columns={columns} rows={statuses} loading={isLoading} emptyMessage="No statuses configured" />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{editing ? `Edit status — ${editing.name}` : "New Status"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth required />
            <TextField select label="Group" value={form.state_group} onChange={(e) => setForm({ ...form, state_group: e.target.value })} fullWidth>
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
              <MenuItem value="closed">Closed</MenuItem>
            </TextField>
            <TextField
              label="Sort order" type="number" value={form.sort_order} fullWidth
              onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })}
            />
            <FormControlLabel
              control={<Switch checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} />}
              label="Default status for new tickets"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDialogOpen(false)} color="inherit">Cancel</Button>
          <Button variant="contained" disabled={!form.name || saveMutation.isPending} onClick={() => saveMutation.mutate()}>
            {editing ? "Save changes" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete status?"
        message={`This deletes "${deleteTarget?.name}". Statuses still used by tickets, marked default, or referenced by a configured transition can't be deleted.`}
        confirmLabel="Delete" danger
        loading={deleteMutation.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
      />
    </Box>
  );
}

function TransitionsTab() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { data: statuses, isLoading: statusesLoading } = useQuery({ queryKey: ["statuses"], queryFn: slaService.listStatuses });
  const { data: transitions, isLoading: transitionsLoading } = useQuery({
    queryKey: ["workflow-transitions"], queryFn: slaService.listTransitions,
  });

  const [pairs, setPairs] = useState(new Set());

  useEffect(() => {
    if (transitions) {
      setPairs(new Set(transitions.map((t) => `${t.from_status_id}-${t.to_status_id}`)));
    }
  }, [transitions]);

  const saveMutation = useMutation({
    mutationFn: () => {
      const list = [...pairs].map((key) => {
        const [from_status_id, to_status_id] = key.split("-").map(Number);
        return { from_status_id, to_status_id };
      });
      return slaService.setTransitions(list);
    },
    onSuccess: () => {
      enqueueSnackbar("Workflow saved", { variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["workflow-transitions"] });
    },
    onError: (err) => enqueueSnackbar(err.response?.data?.detail || "Couldn't save workflow", { variant: "error" }),
  });

  if (statusesLoading || transitionsLoading) return <Loader />;

  const sorted = [...(statuses || [])].sort((a, b) => a.sort_order - b.sort_order);

  const toggle = (fromId, toId) => {
    const key = `${fromId}-${toId}`;
    setPairs((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Check a box to allow moving a ticket from the row's status to the column's status. If nothing is
        checked at all, the workflow is unrestricted -- any status can move to any status, same as before
        a workflow is configured here.
      </Typography>

      {sorted.length < 2 ? (
        <Typography variant="body2" color="text.secondary">Add at least two statuses to configure transitions.</Typography>
      ) : (
        <Paper variant="outlined" sx={{ overflowX: "auto", p: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>From \ To</TableCell>
                {sorted.map((s) => <TableCell key={s.id} align="center">{s.name}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((from) => (
                <TableRow key={from.id}>
                  <TableCell sx={{ fontWeight: 600 }}>{from.name}</TableCell>
                  {sorted.map((to) => (
                    <TableCell key={to.id} align="center">
                      {from.id === to.id ? (
                        <Tooltip title="A status can't transition to itself">
                          <span>—</span>
                        </Tooltip>
                      ) : (
                        <Checkbox
                          size="small"
                          checked={pairs.has(`${from.id}-${to.id}`)}
                          onChange={() => toggle(from.id, to.id)}
                        />
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Button variant="contained" sx={{ mt: 3 }} disabled={saveMutation.isPending} onClick={() => saveMutation.mutate()}>
        Save Workflow
      </Button>
    </Box>
  );
}
